import { createSignal, onMount, createEffect } from "solid-js";
import { View, Text, hexColor } from "@lightningtv/solid";
import { isRTL } from "../translations/configuration";

export default function PinKeyboard(props: {
    onSubmit: (pin: string) => void;
    onPinChange?: (pinLength: number) => void;
    keyboardRef?: any;
    autoFocus?: boolean;
    onBackLeft?: () => void;
    onDown?: () => void;
    submitRef?: (el: any) => void;
}) {
    const layout = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['0', 'Del'],
    ];

    const refs: Array<Array<any>> = layout.map(() => []);
    const [inputValue, setInputValue] = createSignal("");
    const [lastFocusedPos, setLastFocusedPos] = createSignal({ row: 0, col: 0 });

    createEffect(() => {
        const currentLength = inputValue().length;
        if (props.onPinChange) {
            props.onPinChange(currentLength);
        }
        if (currentLength === 4) {
            setTimeout(() => {
                props.keyboardRef?.focusDoneButton?.();
            }, 100);
        }
    });

    onMount(() => {
        if (props.autoFocus !== false) {
            refs[0][0]?.setFocus?.();
        }

        if (props?.keyboardRef) {
            props.keyboardRef.resetInput = () => {
                setInputValue('');
                setTimeout(() => {
                    refs[0][0]?.setFocus?.();
                    setLastFocusedPos({ row: 0, col: 0 });
                }, 50);
            };

            props.keyboardRef.setFocus = () => {
                const pos = lastFocusedPos();
                refs[pos.row]?.[pos.col]?.setFocus?.();
            };

            props.keyboardRef.submitPin = () => {
                if (inputValue().length === 4) {
                    props.onSubmit(inputValue());
                }
            };

            props.keyboardRef.getInputLength = () => inputValue().length;
        }
    });

    const handleKeyPress = (key: string) => {
        if (key === 'Del') {
            setInputValue((prev) => prev.slice(0, -1));
        } else {
            if (inputValue().length < 4) {
                setInputValue((prev) => prev + key);
            }
        }
    };

    const updateLastFocus = (rowIndex: number, colIndex: number) => {
        setLastFocusedPos({ row: rowIndex, col: colIndex });
    };

    const KEY_W = 110;
    const KEY_H = 80;
    const KEY_GAP_X = 16;
    const KEY_GAP_Y = 14;
    // Del key spans 2 key widths + 1 gap between them
    const DEL_W = KEY_W * 2 + KEY_GAP_X;

    return (
        <View
            x={0} y={0}
            width={KEY_W * 3 + KEY_GAP_X * 2 + 40}
            height={KEY_H * 4 + KEY_GAP_Y * 3 + 40}
            borderRadius={24}
            color={hexColor("#0d0f10")}
        >
            <View x={20} y={20}>
                {layout.map((row, rowIndex) => (
                    <View y={rowIndex * (KEY_H + KEY_GAP_Y)}>
                        {row.map((key, colIndex) => {
                            const isDel = key === 'Del';
                            const isLastRow = rowIndex === layout.length - 1;
                            const isLastCol = colIndex === row.length - 1;
                            const keyWidth = isDel ? DEL_W : KEY_W;

                            return (
                                <View
                                    ref={(el) => (refs[rowIndex][colIndex] = el)}
                                    x={colIndex * (KEY_W + KEY_GAP_X)}
                                    y={0}
                                    width={keyWidth}
                                    height={KEY_H}
                                    borderRadius={16}
                                    style={{
                                        color: hexColor("#1c1e22"),
                                        focus: { color: hexColor("#363a45") },
                                        transition: { color: { duration: 150 } },
                                    }}
                                    onEnter={() => handleKeyPress(key)}
                                    onFocus={() => updateLastFocus(rowIndex, colIndex)}
                                    onRight={() => {
                                        if (isRTL() && colIndex === 2) {
                                            props.onBackLeft?.();
                                        } else if (!isRTL() && isLastRow && isLastCol) {
                                            props.keyboardRef?.focusEnterButton?.();
                                        } else {
                                            updateLastFocus(rowIndex, colIndex + 1);
                                            refs[rowIndex][colIndex + 1]?.setFocus?.();
                                        }
                                    }}
                                    onLeft={() => {
                                        if (!isRTL() && colIndex === 0) {
                                            props.onBackLeft?.();
                                        } else if (isRTL() && colIndex === 0) {
                                            return;
                                        } else {
                                            updateLastFocus(rowIndex, colIndex - 1);
                                            refs[rowIndex][colIndex - 1]?.setFocus?.();
                                        }
                                    }}
                                    onDown={() => {
                                        if (isLastRow && props.onDown && inputValue().length < 4) {
                                            props.onDown();
                                        } else if (!isLastRow) {
                                            updateLastFocus(rowIndex + 1, colIndex);
                                            refs[rowIndex + 1]?.[colIndex]?.setFocus?.();
                                        }
                                    }}
                                    onUp={() => {
                                        updateLastFocus(rowIndex - 1, colIndex);
                                        refs[rowIndex - 1]?.[colIndex]?.setFocus?.();
                                    }}
                                >
                                    {isDel ? (
                                        // Icon + "Delete" text, centered inside the double-wide key
                                        <>
                                            <View
                                                x={Math.floor((DEL_W / 2) - 45)}
                                                y={25}
                                                src="https://i.ibb.co/wh6n5N3X/backspace.png"
                                                width={30} height={30}
                                            />
                                            <Text
                                                x={Math.floor((DEL_W / 2) - 10)}
                                                y={28}
                                                style={{ fontSize: 22, color: hexColor("#e2e8f0") }}
                                            >
                                                Delete
                                            </Text>
                                        </>
                                    ) : (
                                        // Number — white normally, black when focused
                                        <Text
                                            x={Math.floor((KEY_W - 20) / 2)}
                                            y={Math.floor((KEY_H - 38) / 2)}
                                            style={{
                                                fontSize: 36,
                                                color: hexColor("#e2e8f0"),
                                                fontWeight: isRTL() ? "normal" : "bold",
                                                focus: { color: hexColor("#010101") },
                                            }}
                                        >
                                            {key}
                                        </Text>
                                    )}
                                </View>
                            );
                        })}
                    </View>
                ))}
            </View>
        </View>
    );
}